import { useState, useEffect, useMemo, useRef } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { isEqual } from 'lodash-es'
import { Plus, Save, Trash2, ExternalLink, Edit, Copy, MoreVertical } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import { CampaignSelector } from '@/components/features/CampaignSelector'
import { CountrySelector } from '@/features/countrySelector'
import { LiveKitCallButton } from '@/features/liveKitCallButton'
import { VapiCallButton } from '@/features/vapiCallButton'
import { ChatbotButton } from '@/components/features/ChatbotButton'

import { useShowDialog, UnsavedChangesDialogTexts } from '@/hooks/useShowDialog';

import { DialogNewCampaign, type NewCampaignData } from './DialogNewCampaign';
import { DispositionsTable } from './DispositionsTable';
import { MetricsConfigTable } from './MetricsConfigTable';
import { IntegrationCards, type IntegrationCardsHandle } from './IntegrationCards';

import { useLLMsList, getPath } from '@/entities/llm';
import { useVoicesList } from '@/entities/voice';
import { useQueryClient } from '@tanstack/react-query'
import {
  useCampaignsFull,
  useSaveCampaign,
  useDeleteCampaign,
  useDuplicateCampaign,
  useCampaignSelection,
} from '@/entities/campaign'
import { useUserData } from "@/entities/user";

import {
  REQUIRED_DISPOSITIONS,
  DEFAULT_CUSTOM_DISPOSITIONS,
  type Agent,
  type Disposition,
  type MetricConfig,
  type CampaignFull,
} from '@intone/shared/types'

const CAMPAIGN_TYPE_LABELS: Record<string, string> = {
  twilio: 'Phone Inbound',
  asterisk: 'Phone Outbound',
  custom: 'Text Inbound',
}

const getEmptyCampaign = (defaultAgent: Agent): CampaignFull => ({
  id: 0,
  name: '',
  alt_key: null,
  telephony: null,
  direction: 'outbound',
  language: 'en',
  country: null,
  inbound_phone_number: null,
  dialer_is_active: false,
  agents: [defaultAgent],
  dispositions: [...REQUIRED_DISPOSITIONS, ...DEFAULT_CUSTOM_DISPOSITIONS],
  dispoConfig: null,
  metrics: [],
})

export function CampaignsPage() {
  const queryClient = useQueryClient()
  const { data: campaigns = [], isLoading: campaignsLoading } = useCampaignsFull()
  const { data: voices = [] } = useVoicesList()
  const { data: llms = [] } = useLLMsList()
  const saveCampaign = useSaveCampaign()
  const { isSuperadmin } = useUserData()
  const deleteCampaign = useDeleteCampaign()
  const duplicateCampaign = useDuplicateCampaign()

  const { campaignId: sharedCampaignId, setCampaignId: setSharedCampaignId } = useCampaignSelection()

  const campaignsKey = campaigns.map(c => `${c.id}|${c.name}`).join(',')
  const campaignsForSelector = useMemo(
    () => campaigns.map(c => ({ id: c.id, name: c.name, dialer_is_active: c.dialer_is_active, telephony: c.telephony })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [campaignsKey]
  )

  const defaultVoice = voices.find(voice => voice["alt_key"] === "br_14");
  const defaultLlm = llms[0];
  const defaultAgent: Agent = useMemo(() => ({
    id: '1',
    agent_name: '',
    voice: defaultVoice?.alt_key || '',
    llm_id: defaultLlm?.id || undefined,
    percentage: 100,
    experiment: '',
  }), [defaultVoice, defaultLlm]);

  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null)
  const [campaign, setCampaign] = useState<CampaignFull>(getEmptyCampaign(defaultAgent))
  const savedCampaignRef = useRef<CampaignFull>(campaign)
  const integrationsRef = useRef<IntegrationCardsHandle>(null)

  const isDirty = !isEqual(campaign, savedCampaignRef.current)
  const { showConfirmDialog } = useShowDialog(isDirty)

  const [showNewCampaignDialog, setShowNewCampaignDialog] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (sharedCampaignId && selectedCampaignId !== sharedCampaignId) {
      setSelectedCampaignId(sharedCampaignId)
    }
  }, [sharedCampaignId, selectedCampaignId])

  const totalPercentage = campaign.agents.reduce(
    (sum, agent) => sum + (Number(agent.percentage) || 0),
    0
  )
  const isValidPercentage = totalPercentage === 100
  const agentsConfigOk = isValidPercentage

  useEffect(() => {
    if (selectedCampaignId) {
      const selected = campaigns.find((c) => c.id === selectedCampaignId)
      if (selected) {
        setCampaign(selected)
        savedCampaignRef.current = selected
      }
    }
  }, [selectedCampaignId, campaigns])

  const validateCampaign = (): boolean => {
    if (!campaign.name?.trim()) {
      toast.error('Campaign name is required')
      return false
    }
    const existingCampaign = campaigns.find(
      (c) => c.name.toLowerCase() === campaign.name.toLowerCase() && c.id !== campaign.id
    )
    if (existingCampaign) {
      toast.error('A campaign with this name already exists')
      return false
    }
    if (!isValidPercentage) {
      toast.error('Agent percentages must add up to 100%')
      return false
    }
    const invalidAgents = campaign.agents.filter(
      (agent) => !agent.agent_name || !agent.llm_id || !agent.voice
    )
    if (invalidAgents.length > 0) {
      toast.error('All agents must have name, LLM and voice selected')
      return false
    }
    const invalidDispositions = campaign.dispositions.filter(
      (dispo) => dispo.isDeletable === true && (!dispo.code || !dispo.description)
    )
    if (invalidDispositions.length > 0) {
      toast.error('All custom dispositions must have both code and description')
      return false
    }
    return true
  }

  const handleOpenNewDialog = () => {
    if (isDirty && !showConfirmDialog(UnsavedChangesDialogTexts.campaignsCreate)) return
    setShowNewCampaignDialog(true)
  }

  const handleNewCampaignConfirm = async (data: NewCampaignData) => {
    setShowNewCampaignDialog(false)
    setIsCreating(true)
    try {
      const result = await saveCampaign.mutateAsync({
        id: 0,
        name: data.name,
        direction: data.direction,
        language: 'en',
        country: data.country,
        telephony: data.telephony,
        agents: [],
        dispositions: [],
        metrics: [],
      })
      toast.success('Campaign created successfully')
      if (result.campaignId) {
        await queryClient.invalidateQueries({ queryKey: ['campaigns-full'] })
        setSelectedCampaignId(result.campaignId)
        setSharedCampaignId(result.campaignId)
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create campaign')
    } finally {
      setIsCreating(false)
    }
  }

  const handleCampaignChange = (id: number | null) => {
    if (id === selectedCampaignId) return
    if (isDirty && !showConfirmDialog(UnsavedChangesDialogTexts.campaignsChange)) return
    setSelectedCampaignId(id)
    setSharedCampaignId(id)
  }

  const updateAgent = (id: string, field: keyof Agent, value: unknown) => {
    setCampaign((prev) => ({
      ...prev,
      agents: prev.agents.map((agent) =>
        agent.id === id ? { ...agent, [field]: value } : agent
      ),
    }))
  }

  const removeAgent = (id: string) => {
    setCampaign((prev) => ({
      ...prev,
      agents: prev.agents.filter((agent) => agent.id !== id),
    }))
  }

  const addAgent = () => {
    const newAgent: Agent = {
      ...defaultAgent,
      id: crypto.randomUUID(),
      percentage: campaign.agents.length === 0 ? 100 : 0,
    }
    setCampaign((prev) => ({
      ...prev,
      agents: [...prev.agents, newAgent],
    }))
  }

  const handleDispositionsChange = (newDispositions: Disposition[]) => {
    setCampaign((prev) => ({ ...prev, dispositions: newDispositions }))
  }

  const handleMetricsChange = (newMetrics: MetricConfig[]) => {
    setCampaign((prev) => ({ ...prev, metrics: newMetrics }))
  }

  const handleSubmit = async () => {
    if (!validateCampaign()) return
    integrationsRef.current?.save()
    try {
      await saveCampaign.mutateAsync({
        id: campaign.id,
        name: campaign.name,
        direction: campaign.direction,
        language: campaign.language,
        country: campaign.country,
        telephony: campaign.telephony,
        agents: campaign.agents,
        dispositions: campaign.dispositions,
        metrics: campaign.metrics,
      })
      toast.success('Campaign updated successfully')
      savedCampaignRef.current = campaign
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save campaign')
    }
  }

  const handleDeleteCampaign = async () => {
    if (!selectedCampaignId) return
    const confirmed = window.confirm(
      `Delete campaign "${campaign.name}"? This will permanently delete all lists and leads associated with this campaign. This action cannot be undone.`
    )
    if (!confirmed) return
    try {
      await deleteCampaign.mutateAsync(selectedCampaignId)
      toast.success('Campaign deleted successfully')
      setSelectedCampaignId(null)
      const empty = getEmptyCampaign(defaultAgent)
      setCampaign(empty)
      savedCampaignRef.current = empty
      setSharedCampaignId(null)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete campaign')
    }
  }

  const handleDuplicateCampaign = async () => {
    if (!selectedCampaignId) return
    try {
      const result = await duplicateCampaign.mutateAsync(selectedCampaignId)
      toast.success('Campaign duplicated successfully')
      if (result.campaignId) {
        setSelectedCampaignId(result.campaignId)
        setSharedCampaignId(result.campaignId)
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to duplicate campaign')
    }
  }

  if (!campaignsLoading && campaigns.length === 0) {
    return (
      <div className="w-full h-full p-4 overflow-y-auto">
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <h1 className="text-heading font-bold mb-1">No Campaigns Found</h1>
            <p className="text-detail text-muted-foreground mb-4">
              Create your first campaign to get started
            </p>
            <Button onClick={() => setShowNewCampaignDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Campaign
            </Button>
          </div>
        </div>
        <DialogNewCampaign
          isOpen={showNewCampaignDialog}
          onClose={() => setShowNewCampaignDialog(false)}
          onConfirm={handleNewCampaignConfirm}
          existingNames={campaigns.map(c => c.name)}
        />
      </div>
    )
  }

  return (
    <div className="w-full h-full p-4 overflow-y-auto">
      {/* Header */}
      <div className="mb-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
          <div className="flex-shrink-0">
            <h1 className="text-heading font-bold mb-1">Campaign Management</h1>
            <p className="text-detail text-muted-foreground">
              Create and manage your call campaigns
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 items-center">
            <Button
              className="w-full sm:w-auto"
              type="button"
              variant="outline"
              onClick={handleOpenNewDialog}
              disabled={isCreating}
            >
              <Plus className="w-3 h-3" />
              New
            </Button>
            <CampaignSelector
              campaigns={campaignsForSelector}
              selectedCampaignId={selectedCampaignId}
              onCampaignChange={handleCampaignChange}
              className="w-full sm:w-80 lg:w-96"
              disabled={isCreating}
            />
            <Button
              type="button"
              variant="default"
              className="w-full sm:w-auto"
              disabled={saveCampaign.isPending || !selectedCampaignId}
              onClick={handleSubmit}
            >
              <Save className="w-3 h-3" />
              {saveCampaign.isPending ? 'Saving...' : 'Save Campaign'}
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {/* Campaign Settings */}
        <Card>
          <CardHeader className="border-b py-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CardTitle className="text-body">Campaign Settings</CardTitle>
                {campaign.alt_key && (
                  <span className="text-detail text-muted-foreground font-normal">{campaign.alt_key}</span>
                )}
              </div>
              {selectedCampaignId && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={handleDuplicateCampaign}
                      disabled={duplicateCampaign.isPending}
                    >
                      <Copy className="w-3 h-3 mr-2" />
                      {duplicateCampaign.isPending ? 'Duplicating...' : 'Duplicate campaign'}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleDeleteCampaign}
                      disabled={deleteCampaign.isPending}
                      className="focus:text-destructive"
                    >
                      <Trash2 className="w-3 h-3 mr-2" />
                      {deleteCampaign.isPending ? 'Deleting...' : 'Delete campaign'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </CardHeader>

          <CardContent className="pt-4 pb-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
              <div className="space-y-1">
                <label className="text-detail font-medium text-muted-foreground">Name</label>
                <Input
                  value={campaign.name}
                  onChange={(e) => setCampaign((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Campaign Name"
                  className="h-8"
                />
                {campaign.direction === 'inbound' && campaign.inbound_phone_number && (
                  <div className="text-detail text-muted-foreground">
                    Phone: {campaign.inbound_phone_number}
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-detail font-medium text-muted-foreground">Type</label>
                <div className="h-8 flex items-center rounded-md border bg-muted/50 px-3 text-detail">
                  {campaign.telephony ? CAMPAIGN_TYPE_LABELS[campaign.telephony] ?? campaign.telephony : '—'}
                </div>
              </div>

              {campaign.telephony !== 'custom' && (
                <div className="space-y-1">
                  <label className="text-detail font-medium text-muted-foreground">Country</label>
                  <CountrySelector
                    value={campaign.country}
                    onChange={(country) => setCampaign((prev) => ({ ...prev, country }))}
                  />
                  <p className="text-detail text-muted-foreground">
                    Used for phone number formatting
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Agents */}
        <Card>
          <CardHeader className="border-b py-2">
            <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-body">
              <span>Agents</span>
              {campaign.agents.length > 0 && (
                <span
                  className={`text-detail font-normal ${agentsConfigOk ? 'text-green-600' : 'text-destructive'}`}
                >
                  {agentsConfigOk
                    ? 'Config ok!'
                    : `Calls % should sum to 100%. Now — ${totalPercentage}%`}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2 pb-3">
            <div className="space-y-3">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[100px] max-w-[150px]">Name</TableHead>
                      <TableHead className="min-w-[120px] max-w-[200px]">LLM</TableHead>
                      <TableHead className="min-w-[100px] max-w-[150px]">Voice</TableHead>
                      <TableHead className="min-w-[60px] max-w-[80px]">% Calls</TableHead>
                      <TableHead className="min-w-[100px] max-w-[150px]">Experiment</TableHead>
                      <TableHead className="min-w-[60px] max-w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {campaign.agents.map((agent) => (
                      <TableRow key={agent.id}>
                        <TableCell className="py-1 min-w-[100px] max-w-[150px]">
                          <Input
                            value={agent.agent_name}
                            onChange={(e) => updateAgent(agent.id, 'agent_name', e.target.value)}
                            placeholder="e.g. Rachel"
                            className="h-7 w-full"
                          />
                        </TableCell>
                        <TableCell className="py-1 min-w-[120px] max-w-[200px]">
                          <div className="flex gap-1">
                            <Select
                              value={agent.llm_id?.toString() || ''}
                              onValueChange={(value) =>
                                updateAgent(agent.id, 'llm_id', value ? parseInt(value) : undefined)
                              }
                            >
                              <SelectTrigger className="h-7 w-full">
                                <SelectValue placeholder="Select LLM" />
                              </SelectTrigger>
                              <SelectContent>
                                {llms.map((llm) => (
                                  <SelectItem key={llm.id} value={llm.id.toString()}>
                                    <span className="truncate block text-left">
                                      {llm.name || 'Unnamed'} - {llm.model}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {agent.llm_id && (
                              <Link
                                to={getPath(agent.llm_id.toString())}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center h-7 w-7 p-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md flex-shrink-0"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </Link>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="py-1 min-w-[100px] max-w-[150px]">
                          <Select
                            value={agent.voice}
                            onValueChange={(value) => updateAgent(agent.id, 'voice', value)}
                          >
                            <SelectTrigger className="h-7 w-full">
                              <SelectValue placeholder="Select voice" />
                            </SelectTrigger>
                            <SelectContent>
                              {voices.map((voice) => (
                                <SelectItem key={voice.alt_key} value={voice.alt_key}>
                                  <span className="truncate block text-left">
                                    {voice.name} ({voice.alt_key})
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="py-1 min-w-[60px] max-w-[80px]">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={agent.percentage}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) =>
                              updateAgent(agent.id, 'percentage', e.target.value)
                            }
                            placeholder="50"
                            className="w-full h-7"
                          />
                        </TableCell>
                        <TableCell className="py-1 min-w-[100px] max-w-[150px]">
                          <Input
                            value={agent.experiment}
                            onChange={(e) => updateAgent(agent.id, 'experiment', e.target.value)}
                            placeholder="e.g. A/B test variant"
                            maxLength={100}
                            className="h-7 w-full"
                          />
                        </TableCell>
                        <TableCell className="py-1 min-w-[60px] max-w-[80px]">
                          <div className="flex gap-1">
                            {agent.id != "1" && (
                              <Link
                                to={`/agents?agent_id=${agent.id}`}
                                className="inline-flex items-center justify-center h-6 w-6 p-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
                                title="Edit agent configuration"
                              >
                                <Edit className="w-4 h-4" />
                              </Link>
                            )}
                            {campaign.id !== 0 && agent.vapiAgentId && (
                              <VapiCallButton
                                vapiAgentId={agent.vapiAgentId}
                                agentId={agent.id}
                                disabled={!agent.agent_name}
                              />
                            )}
                            {campaign.id !== 0 && !agent.vapiAgentId && (
                              <LiveKitCallButton
                                campaignId={campaign.id}
                                agentId={agent.id}
                                disabled={!agent.agent_name || !agent.llm_id || !agent.voice}
                              />
                            )}
                            {isSuperadmin && campaign.id !== 0 && !agent.vapiAgentId && (
                              <ChatbotButton
                                campaignId={campaign.id}
                                agentId={agent.id}
                                disabled={!agent.agent_name || !agent.llm_id || !agent.voice}
                              />
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeAgent(agent.id)}
                              disabled={campaign.agents.length <= 1}
                              className="h-6 w-6 p-0 text-gray-400 hover:text-destructive"
                            >
                              <Trash2 className="w-2.5 h-2.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <Button variant="outline" size="sm" className="h-7" onClick={addAgent}>
                <Plus className="w-3 h-3" />
                Add Agent
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Dispositions */}
        {campaign.dispositions.length > 0 && (
          <DispositionsTable
            dispositions={campaign.dispositions}
            onDispositionsChange={handleDispositionsChange}
          />
        )}

        {/* Post-Call QA Metrics */}
        <MetricsConfigTable
          metrics={campaign.metrics}
          onMetricsChange={handleMetricsChange}
        />

        {/* Integrations */}
        {selectedCampaignId && selectedCampaignId > 0 && (
          <IntegrationCards ref={integrationsRef} campaignId={selectedCampaignId} />
        )}
      </div>

      <DialogNewCampaign
        isOpen={showNewCampaignDialog}
        onClose={() => setShowNewCampaignDialog(false)}
        onConfirm={handleNewCampaignConfirm}
        existingNames={campaigns.map(c => c.name)}
      />
    </div>
  )
}